/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';

import ReviewAccordionTitle from '../ReviewAccordionTitle';

function initialOpenTabs(data: ReviewAccordionItem[]): number[] {
  const indexArr = [0];
  const newArr = [...data];
  newArr.shift();

  newArr.forEach((item, i) => {
    if (item.shouldOpenOnInitialLoad) {
      indexArr.push(i + 1);
    }
  });

  return indexArr;
}

type ReviewAccordionBody =
  | ReactNode
  | Array<
      | ReactNode
      | ((
          // function child returning JSX
          ...args: any[]
        ) => ReactNode)
    >;

interface ReviewAccordionItem {
  title?: string;
  count?: number;
  Subtitle?: ReactNode;
  body?: ReviewAccordionBody;
  shouldOpenOnInitialLoad?: boolean;
}

interface ReviewAccordionProps {
  data?: ReviewAccordionItem[];
  canRunInitialOpenTabs?: boolean;
  initializer?: () => number[];
  canHaveMultipleOpen?: boolean;
  shouldScrollToTopOnChange?: boolean;
  customTitle?: boolean;
  fullWidth?: boolean;
  editMode?: Record<string, boolean>;
}

const ReviewAccordion = ({
  data = [],
  canRunInitialOpenTabs,
  initializer,
  canHaveMultipleOpen,
  shouldScrollToTopOnChange,
  customTitle,
  fullWidth,
  editMode = {}
}: ReviewAccordionProps): JSX.Element => {
  const [hasRunInitialOpenTabs, setHasRunInitialOpenTabs] =
    useState(false);

  const [openIndexes, setOpenIndexes] = useState<number[]>(
    () => (initializer ? initializer() : [0])
  );

  useEffect(() => {
    if (canRunInitialOpenTabs && !hasRunInitialOpenTabs) {
      setHasRunInitialOpenTabs(true);
      setOpenIndexes(initialOpenTabs(data));
    }
  }, [canRunInitialOpenTabs, data, hasRunInitialOpenTabs]);

  const onTitleClick = (index: number) => {
    if (canHaveMultipleOpen) {
      setOpenIndexes(
        openIndexes.includes(index)
          ? openIndexes.filter(i => i !== index)
          : [...openIndexes, index]
      );
    } else {
      setOpenIndexes(
        openIndexes.includes(index) ? [] : [index]
      );
    }

    shouldScrollToTopOnChange &&
      window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  return (
    <Box sx={{ pb: 2 }}>
      {data.map(({ title, count, Subtitle, body }, i) => (
        <Accordion
          key={`${i}+${title}`}
          expanded={openIndexes.includes(i)}
          onChange={() => onTitleClick(i)}
          sx={{
            mb: 3,
            padding: 0,
            border: '1px solid #DBDDDC',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ padding: '16px' }}
          >
            <Box sx={{ width: '100%' }}>
              <ReviewAccordionTitle
                onTitleClick={() => onTitleClick(i)}
                title={title}
                count={count}
                customTitle={customTitle}
                fullWidth={fullWidth}
                Subtitle={Subtitle}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            {Array.isArray(body)
              ? body
                  .filter(Boolean)
                  .map((child, idx) => {
                    if (typeof child === 'function') {
                      const rendered = child();
                      if (!rendered) return null;

                      const cardKey =
                        (child as any).__reviewCardKey;

                      const editModeKey =
                        cardKey === 'address'
                          ? 'shipTo'
                          : cardKey;

                      const isEditing = !!(
                        editModeKey &&
                        editMode[editModeKey]
                      );

                      return (
                        <Box
                          key={
                            cardKey
                              ? `${i}-${idx}-${cardKey}`
                              : `${i}-${idx}-func`
                          }
                          sx={{
                            px: '16px',
                            py: '24px'
                          }}
                        >
                          <div
                            className={
                              isEditing
                                ? 'review-labels--edit'
                                : undefined
                            }
                          >
                            {rendered}
                          </div>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={`${i}-${idx}-child`}
                        sx={{
                          px: '16px',
                          py: '24px'
                        }}
                      >
                        {child}
                      </Box>
                    );
                  })
                  .filter(Boolean)
              : body}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ReviewAccordion;